import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  PlusCircle,
  Trash2,
  Search,
} from "lucide-react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import Navbar from "./Navbar";

const TeamManager = () => {
  const [doctors, setDoctors] = useState([]);
  const [newDoctors, setNewDoctors] = useState([
    {
        doctor_name: "",
        doctor_email: "",
        phone_number: "",
        registration_No: "",
        speciality: "",
        degree_name: "",
        medical_license_no: "",
    },
  ]);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetchDoctors();
  }, []);

  const fetchDoctors = async () => {
    try {
      const res = await axios.get("https://www.careplus.gopillz.com/add_team_doctor", {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      setDoctors(res.data.doctors);
    } catch (err) {
      console.error("Error fetching doctors:", err);
    }
  };

  const handleChange = (index, field, value) => {
    const updated = [...newDoctors];
    updated[index][field] = value;
    setNewDoctors(updated);
  };

  const addDoctorField = () => {
    setNewDoctors([
      ...newDoctors,
      {
        doctor_name: "",
        doctor_email: "",
        phone_number: "",
        registration_No: "",
        speciality: "",
        degree_name: "",
        medical_license_no: "",
      },
    ]);
  };

  const removeDoctorField = (index) => {
    const updated = [...newDoctors];
    updated.splice(index, 1);
    setNewDoctors(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");
    try {
      await axios.post(
        "https://www.careplus.gopillz.com/add_team_doctor",
        { doctors: newDoctors },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      setMessage("✅ Doctor(s) added successfully!");
      setNewDoctors([
        {
            doctor_name: "",
            doctor_email: "",
            phone_number: "",
            registration_No: "",
            speciality: "",
            degree_name: "",
            medical_license_no: "",
        },
      ]);
      fetchDoctors();
    } catch (err) {
      setMessage("❌ Failed to add doctors. Try again.");
    }
    setLoading(false);
  };

  const filteredDoctors = doctors.filter((doc) =>
    [
      doc.name,
      doc.email,
      doc.phone_number,
      doc.speciality,
    ]
      .join(" ")
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  return (
    <div className="bg-blue-50 min-h-screen">
      <Navbar />
      <div className="flex justify-center items-start px-4 pt-24 pb-10">
        <div className="w-full max-w-6xl space-y-10">
          
          {/* Header */}
          <div className="text-center">
            <h1 className="text-4xl font-bold text-blue-700 mb-2">👨‍⚕️ Doctor Team Manager</h1>
            <p className="text-gray-600">Manage team doctors efficiently</p>
          </div>

          {/* Search Bar */}
          <div className="flex justify-center">
            <div className="relative w-full sm:w-1/2">
              <input
                type="text"
                placeholder="Search by doctor name, phone, email,speciality "
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-10 py-2 focus:ring-2 focus:ring-blue-400 outline-none"
              />
              <Search className="absolute left-3 top-2.5 text-gray-500 w-5 h-5" />
            </div>
          </div>

          {/* Doctor List */}
          <section className="bg-white rounded-2xl shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Existing Doctors</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredDoctors.length === 0 ? (
                <p className="text-gray-500 col-span-full">No doctors found.</p>
              ) : (
                filteredDoctors.map((doc, index) => (
                  <div
                    key={index}
                    className="bg-blue-100 border border-blue-200 rounded-xl p-4 hover:shadow-lg transition"
                  >
                    <h3 className="text-lg font-semibold text-blue-900">{doc.name}</h3>
                    <p className="text-sm text-blue-800">✉️ {doc.email}</p>
                    <p className="text-sm text-blue-800">📞 {doc.phone_number}</p>
                    <p className="text-sm text-blue-800">🩺 {doc.speciality}</p>
                    <p className="text-sm text-blue-800">🎓 {doc.degree_name}</p> {/* ✅ Added */}
                  </div>
                ))
              )}
            </div>
          </section>

          {/* Add Doctor Form */}
          <section className="bg-white rounded-2xl shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-6">➕ Add New Doctor(s)</h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              {newDoctors.map((doc, index) => (
                <div key={index} className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <input
                    type="text"
                    placeholder="Doctor Name"
                    value={doc.doctor_name}
                    onChange={(e) => handleChange(index, "doctor_name", e.target.value)}
                    required
                    className="border border-gray-300 rounded-lg px-4 py-2"
                  />
                  <input
                    type="email"
                    placeholder="Doctor Email"
                    value={doc.doctor_email}
                    onChange={(e) => handleChange(index, "doctor_email", e.target.value)}
                    className="border border-gray-300 rounded-lg px-4 py-2"
                  />
                  <PhoneInput
                    country="in"
                    enableSearch
                    value={doc.phone_number}
                    onChange={(value) => handleChange(index, "phone_number", value)}
                    inputClass="!w-full !px-4 !py-2 !border !border-gray-300 !rounded-lg !bg-gray-50"
                    buttonClass="!border-gray-300"
                    containerClass="!w-full"
                  />
                  <input
                    type="text"
                    placeholder="Registration No."
                    value={doc.registration_No}
                    onChange={(e) => handleChange(index, "registration_No", e.target.value)}
                    className="border border-gray-300 rounded-lg px-4 py-2"
                  />
                  <input
                    type="text"
                    placeholder="Speciality"
                    value={doc.speciality}
                    onChange={(e) => handleChange(index, "speciality", e.target.value)}
                    className="border border-gray-300 rounded-lg px-4 py-2"
                  />
                  <input
                    type="text"
                    placeholder="Degree Name"
                    value={doc.degree_name}
                    onChange={(e) => handleChange(index, "degree_name", e.target.value)}
                    className="border border-gray-300 rounded-lg px-4 py-2"
                  />
                  <input
                    type="text"
                    placeholder="Medical License No."
                    value={doc.medical_license_no}
                    onChange={(e) => handleChange(index, "medical_license_no", e.target.value)}
                    className="border border-gray-300 rounded-lg px-4 py-2"
                  />
                  {newDoctors.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeDoctorField(index)}
                      className="text-red-500 hover:text-red-700 text-sm flex items-center justify-center"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  )}
                </div>
              ))}

              <div className="flex flex-wrap items-center gap-4 mt-2">
                <button
                  type="button"
                  onClick={addDoctorField}
                  className="flex items-center gap-2 bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition"
                >
                  <PlusCircle className="w-5 h-5" /> Add Another
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition"
                >
                  {loading ? "Saving..." : "Submit Doctor(s)"}
                </button>
              </div>
              {message && <p className="text-blue-600 mt-2">{message}</p>}
            </form>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TeamManager;

